/**
 * Parse BBO (Bridge Base Online) Team Match data
 * Handles HTML from BBO team match pages (tview.php)
 */

import { calculateVPs } from './vpCalculator';
import { mapPlayerNames } from './playerNameMapper';

/**
 * Extract BBO match data from HTML string
 * @param {string} htmlText - Raw HTML from BBO team match page
 * @param {string} url - Original BBO URL (for metadata)
 * @returns {Promise<Object>} - Parsed match data in application format
 */
export async function parseBBOTeamMatch(htmlText, url = '') {
  // Create a DOM parser
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlText, 'text/html');

  // Extract match information
  const matchInfo = extractMatchInfo(doc, url);

  // Extract player names
  const players = extractPlayerNames(doc);
  matchInfo.players = players;

  // Apply name mapping to players
  const mappedPlayers = await applyNameMappingToPlayers(players);

  // Extract board results
  const boards = extractBoardResults(doc);

  // Calculate team scores
  const teamScores = await calculateTeamScoresWithMapping(boards, matchInfo, mappedPlayers);

  // Convert to travellers format
  const travellers = await convertToTravellersWithMapping(boards, mappedPlayers);

  // Look for hand records (if available)
  const handRecordLinks = extractHandRecordLinks(doc, url);
  console.log('Extracted hand record links:', handRecordLinks);

  // Extract hand records from LIN data
  const handRecords = extractHandRecordsFromLinks(doc);

  return {
    metadata: {
      Title: matchInfo.title,
      Date: formatDate(matchInfo.date),
      Boards: matchInfo.numBoards.toString(),
      Source: 'BBO',
      URL: url,
      HandRecordLinks: handRecordLinks
    },
    scores: teamScores,
    travellers: travellers,
    gameType: 'teams',
    handRecords: handRecords,
    rawMatchInfo: matchInfo
  };
}

/**
 * Extract basic match information from the page
 */
function extractMatchInfo(doc, url) {
  const info = {
    title: 'BBO Team Match',
    date: new Date().toISOString().split('T')[0],
    numBoards: 0,
    team1: 'Team 1',
    team2: 'Team 2',
    players: {}
  };

  // Try to find title from page
  const titleElement = doc.querySelector('h1, h2, h3, .title, .match-title');
  if (titleElement) {
    info.title = titleElement.textContent.trim();
  }

  // Look for title in table (BBO format)
  const titleCells = doc.querySelectorAll('td.bbo_tlv');
  for (const cell of titleCells) {
    const prevCell = cell.previousElementSibling;
    if (prevCell && prevCell.textContent.toLowerCase().includes('title')) {
      info.title = cell.textContent.trim();
      break;
    }
  }

  // Try to find date
  const datePattern = /\b(\d{1,2}[-/]\d{1,2}[-/]\d{2,4}|\w+\s+\d{1,2},?\s+\d{4})\b/;
  const bodyText = doc.body.textContent;
  const dateMatch = bodyText.match(datePattern);
  if (dateMatch) {
    info.date = parseDate(dateMatch[1]);
  }

  // Extract team names from BBO format
  // BBO uses pattern like "EJC: " as label and team members in next cell
  const teamLabels = [];
  const allRows = doc.querySelectorAll('tr');

  for (const row of allRows) {
    const cells = row.querySelectorAll('td.bbo_tll, td.bbo_tlv');
    if (cells.length >= 2) {
      const labelCell = cells[0];
      const valueCell = cells[1];
      const label = labelCell.textContent.trim();

      // Look for team name pattern (letters followed by colon)
      if (/^[A-Z][A-Za-z0-9_]*:\s*$/.test(label)) {
        const teamName = label.replace(':', '').trim();
        const players = valueCell.textContent.trim();

        if (teamLabels.length === 0) {
          info.team1 = teamName;
          teamLabels.push({ name: teamName, players });
        } else if (teamLabels.length === 1) {
          info.team2 = teamName;
          teamLabels.push({ name: teamName, players });
        }
      }
    }
  }

  // Count boards from table
  const tables = doc.querySelectorAll('table');
  for (const table of tables) {
    const rows = table.querySelectorAll('tr');
    // Look for board numbers in first column
    let boardCount = 0;
    for (const row of rows) {
      const firstCell = row.querySelector('td:first-child, th:first-child');
      if (firstCell && /^\d+$/.test(firstCell.textContent.trim())) {
        boardCount++;
      }
    }
    if (boardCount > info.numBoards) {
      info.numBoards = boardCount;
    }
  }

  return info;
}

/**
 * Extract board-by-board results from the main table
 */
function extractBoardResults(doc) {
  const boards = [];
  const tables = doc.querySelectorAll('table');

  console.log(`Found ${tables.length} tables in the document`);

  // Find the main results table
  let mainTable = null;
  let maxRows = 0;

  for (const table of tables) {
    const rows = table.querySelectorAll('tr');
    const firstRow = rows[0];
    if (firstRow && rows.length > 2) {
      const headerText = firstRow.textContent.toLowerCase();
      const cellCount = firstRow.querySelectorAll('th, td').length;

      console.log(`Table with ${rows.length} rows, ${cellCount} columns, header: "${headerText.substring(0, 100)}"`);

      // Look for BBO team match table format
      // BBO uses "#" for board number, "our table" and "their table" for the two rooms
      const isBBOFormat = (
        (headerText.includes('#') || headerText.includes('board') || headerText.includes('bd')) &&
        (headerText.includes('our table') || headerText.includes('their table') ||
         headerText.includes('contract') || headerText.includes('cont')) &&
        cellCount >= 5
      );

      if (isBBOFormat && rows.length > maxRows) {
        mainTable = table;
        maxRows = rows.length;
      }
    }
  }

  if (!mainTable) {
    console.error('Could not find main results table');
    console.log('Available tables:', tables.length);
    return boards;
  }

  console.log(`Using table with ${maxRows} rows`);

  // Parse table rows
  const rows = mainTable.querySelectorAll('tr');
  const headerRow = rows[0];
  const headers = Array.from(headerRow.querySelectorAll('th, td')).map(th =>
    th.textContent.trim().toLowerCase()
  );

  console.log('Headers:', headers);

  // Try different column mapping strategies
  let colMap = findBBOColumns(headers);
  console.log('Column mapping:', colMap);

  // Parse data rows
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    const cells = Array.from(row.querySelectorAll('td, th'));

    if (cells.length === 0) continue;

    // Get all cell text for debugging
    const cellTexts = cells.map(c => c.textContent.trim());

    // BBO tview.php has a 7-column structure in data rows:
    // [0] Board#, [1] Our table contract, [2] Our table score, [3] IMP+, [4] IMP-, [5] Their table contract, [6] Their table score
    // IMPs appear in either column 3 (if positive for us) or column 4 (if negative for us)
    if (cellTexts.length < 7) continue;

    const boardNum = cellTexts[0];
    if (!boardNum || !/^\d+$/.test(boardNum)) continue;

    const ourContract = cellTexts[1] || '';
    const ourScore = cellTexts[2] || '0';

    // Check both IMP columns - one for + (our favor), one for - (their favor)
    const impPlus = cellTexts[3] || '';
    const impMinus = cellTexts[4] || '';
    const impsText = impPlus || (impMinus ? '-' + impMinus : '0');

    const theirContract = cellTexts[5] || '';
    const theirScore = cellTexts[6] || '0';

    console.log(`Board ${boardNum}:`, {
      ourContract,
      ourScore,
      imps: impsText,
      theirContract,
      theirScore,
      allCells: cellTexts
    });

    // Parse BBO contract format (e.g., "2NN+1" = 2NT by N, made +1)
    const contract1 = parseBBOContract(ourContract);
    const contract2 = parseBBOContract(theirContract);

    console.log(`Board ${boardNum} after parsing:`, {
      contract1: contract1.contract,
      contract2: contract2.contract,
      allCells: cellTexts
    });

    const board = {
      boardNumber: parseInt(boardNum),
      contract1: contract1.contract,
      declarer1: contract1.declarer,
      result1: contract1.tricks,
      score1: parseScore(ourScore),
      contract2: contract2.contract,
      declarer2: contract2.declarer,
      result2: contract2.tricks,
      score2: parseScore(theirScore),
      imps: parseIMPs(impsText)
    };

    boards.push(board);
  }

  console.log(`Extracted ${boards.length} boards`);
  return boards;
}

/**
 * Find BBO table columns with multiple strategies
 */
function findBBOColumns(headers) {
  const colMap = {
    board: -1,
    contract1: -1,
    declarer1: -1,
    result1: -1,
    score1: -1,
    contract2: -1,
    declarer2: -1,
    result2: -1,
    score2: -1,
    imps: -1
  };

  // Find board column (BBO uses "#")
  colMap.board = findColumnIndex(headers, ['#', 'board', 'bd', 'brd']);

  // Find "our table" and "their table" sections
  let ourTableIdx = -1;
  let theirTableIdx = -1;

  headers.forEach((h, i) => {
    const lower = h.toLowerCase();
    if (lower.includes('our table') || lower === 'our' || lower.includes('closed')) {
      ourTableIdx = i;
    }
    if (lower.includes('their table') || lower === 'their' || lower.includes('open')) {
      theirTableIdx = i;
    }
  });

  console.log('Our table column:', ourTableIdx, 'Their table column:', theirTableIdx);

  // If we found "our table" and "their table" headers
  if (ourTableIdx >= 0 && theirTableIdx >= 0) {
    // BBO format typically has columns: Contract, By (declarer), Result, Score under each table
    // The columns immediately after the table header are the data columns
    colMap.contract1 = ourTableIdx;
    colMap.declarer1 = ourTableIdx + 1;
    colMap.result1 = ourTableIdx + 2;
    colMap.score1 = ourTableIdx + 3;

    colMap.contract2 = theirTableIdx;
    colMap.declarer2 = theirTableIdx + 1;
    colMap.result2 = theirTableIdx + 2;
    colMap.score2 = theirTableIdx + 3;

    // IMP column is usually after the second table
    colMap.imps = theirTableIdx + 4;
  } else {
    // Fallback: look for contract columns
    const contractIndices = [];
    headers.forEach((h, i) => {
      if (h.includes('cont') || h.includes('contract') || h === 'c') {
        contractIndices.push(i);
      }
    });

    console.log('Contract columns found at indices:', contractIndices);

    if (contractIndices.length >= 2) {
      colMap.contract1 = contractIndices[0];
      colMap.contract2 = contractIndices[1];
      colMap.declarer1 = contractIndices[0] + 1;
      colMap.declarer2 = contractIndices[1] + 1;
      colMap.result1 = contractIndices[0] + 2;
      colMap.result2 = contractIndices[1] + 2;
      colMap.score1 = contractIndices[0] + 3;
      colMap.score2 = contractIndices[1] + 3;
    }
  }

  // Find IMP column if not already found
  if (colMap.imps === -1) {
    colMap.imps = findColumnIndex(headers, ['imp', 'imps', 'diff', 'score (imps)']);
  }

  return colMap;
}

/**
 * Parse BBO contract format
 * Examples: "2NN+1" = 2NT by N, made +1 overtrick
 *           "4SS-3" = 4S by S, down 3
 *           "3HE=" = 3H by E, made exactly
 *           "Pass" = passed out
 */
function parseBBOContract(text) {
  if (!text || text === '' || text.toLowerCase() === 'pass') {
    return { contract: 'Pass', declarer: '', tricks: null };
  }

  // BBO format: LevelSuitDeclarerResult
  // e.g., "2NN+1", "4SS-3", "3HE=", "7NTWx+2"
  const match = text.match(/^(\d)([SHDCN]T?|NT)([NESW])(x{0,2})([+-=]\d*)?$/i);

  if (!match) {
    console.warn('Could not parse BBO contract:', text);
    return { contract: 'Pass', declarer: '', tricks: null };
  }

  const level = match[1];
  let suit = match[2].toUpperCase();
  const declarer = match[3].toUpperCase();
  const double = match[4] ? (match[4].length === 2 ? 'XX' : 'X') : '';
  const resultStr = match[5] || '=';

  // Normalize suit
  if (suit === 'N') suit = 'NT';
  if (suit === 'NT') suit = 'NT';
  else if (suit.length > 1) suit = suit[0]; // Handle "HT" as "H", etc.

  const contract = `${level}${suit}${double}`;

  // Parse result (tricks made)
  const baseTricks = 6 + parseInt(level);
  let tricks = baseTricks;

  if (resultStr.startsWith('+')) {
    const overtricks = parseInt(resultStr.substring(1)) || 0;
    tricks = baseTricks + overtricks;
  } else if (resultStr.startsWith('-')) {
    const undertricks = parseInt(resultStr.substring(1)) || 0;
    tricks = baseTricks - undertricks;
  } else if (resultStr === '=') {
    tricks = baseTricks;
  }

  return {
    contract,
    declarer,
    tricks
  };
}

/**
 * Find column index by searching for keywords
 */
function findColumnIndex(headers, keywords, occurrence = 0) {
  let found = 0;
  for (let i = 0; i < headers.length; i++) {
    const header = headers[i];
    for (const keyword of keywords) {
      if (header.includes(keyword)) {
        if (found === occurrence) {
          return i;
        }
        found++;
        break;
      }
    }
  }
  return -1;
}

/**
 * Parse score
 */
function parseScore(text) {
  if (!text) return 0;
  // Remove non-numeric characters except + and -
  const cleaned = text.replace(/[^0-9+-]/g, '');
  return parseInt(cleaned) || 0;
}

/**
 * Parse IMPs
 */
function parseIMPs(text) {
  if (!text) return 0;
  const cleaned = text.replace(/[^0-9+-]/g, '');
  return parseInt(cleaned) || 0;
}

/**
 * Calculate team scores from board results
 */
/**
 * Extract links to hand records
 */
function extractHandRecordLinks(doc, baseUrl) {
  const links = [];
  const allLinks = doc.querySelectorAll('a[href]');

  for (const link of allLinks) {
    const href = link.getAttribute('href');
    const text = link.textContent.trim();

    // Look for hand/board/deal links
    if (href && (
      href.includes('hand') ||
      href.includes('board') ||
      href.includes('deal') ||
      href.includes('bview') ||
      href.includes('lin=') ||  // LIN format data
      text.toLowerCase().includes('board') ||
      text.toLowerCase().includes('hand')
    )) {
      // Make absolute URL
      let fullUrl = href;
      if (!href.startsWith('http')) {
        try {
          // Try to make absolute URL if baseUrl is valid
          if (baseUrl && baseUrl.startsWith('http')) {
            fullUrl = new URL(href, baseUrl).href;
          } else {
            // Fallback to BBO base URL
            fullUrl = new URL(href, 'https://www.bridgebase.com').href;
          }
        } catch (e) {
          // If URL construction fails, use href as-is
          fullUrl = href;
        }
      }

      // Check if URL contains deal data
      const urlParams = new URLSearchParams(fullUrl.split('?')[1] || '');
      const hasLIN = urlParams.has('lin');
      const hasDeal = urlParams.has('deal');

      links.push({
        url: fullUrl,
        text: text,
        hasLIN: hasLIN,
        hasDeal: hasDeal,
        boardNumber: extractBoardNumberFromLink(text, href)
      });
    }
  }

  console.log(`Found ${links.length} hand record links`);
  if (links.length > 0) {
    console.log('Sample link:', links[0]);
  }

  return links;
}

/**
 * Extract board number from link text or href
 */
function extractBoardNumberFromLink(text, href) {
  // Try to extract board number from text like "Board 1" or just "1"
  const textMatch = text.match(/\b(\d+)\b/);
  if (textMatch) return parseInt(textMatch[1]);

  // Try to extract from URL parameter like b=1
  const urlMatch = href.match(/[?&]b=(\d+)/);
  if (urlMatch) return parseInt(urlMatch[1]);

  return null;
}

/**
 * Parse various date formats
 */
function parseDate(dateStr) {
  // Try different formats
  const formats = [
    // DD/MM/YYYY
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/,
    // MM/DD/YYYY
    /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/,
    // Month DD, YYYY
    /^(\w+)\s+(\d{1,2}),?\s+(\d{4})$/
  ];

  for (const format of formats) {
    const match = dateStr.match(format);
    if (match) {
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      } catch (e) {
        // Try next format
      }
    }
  }

  // Default to today
  return new Date().toISOString().split('T')[0];
}

/**
 * Format date for storage
 */
function formatDate(dateStr) {
  try {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateStr;
  }
}

/**
 * Validate BBO URL
 */
export function isValidBBOUrl(url) {
  try {
    const urlObj = new URL(url);
    return (
      urlObj.hostname.includes('bridgebase.com') &&
      (urlObj.pathname.includes('tview') || urlObj.pathname.includes('vugraph'))
    );
  } catch (e) {
    return false;
  }
}

/**
 * Extract player names from BBO table headers
 */
function extractPlayerNames(doc) {
  const players = {
    ourTable: { N: '', E: '', S: '', W: '' },
    theirTable: { N: '', E: '', S: '', W: '' }
  };

  // Helper to extract player names from all cells in a table
  const extractPlayersFromTable = (table) => {
    const playerNames = [];
    const allCells = table.querySelectorAll('td');

    for (const cell of allCells) {
      // Get text content, excluding nested tables
      let text = '';
      for (const node of cell.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          text += node.textContent;
        }
      }
      text = text.trim();

      // Filter out empty cells, single-letter compass directions, and 'nwes'
      if (text && text.length > 1 && !['nwes', 'nesw', 'ewsn'].includes(text.toLowerCase())) {
        playerNames.push(text);
      }
    }

    return playerNames;
  };

  // Look for player tables in the header
  const playerTables = doc.querySelectorAll('table.players');

  console.log('Player tables found:', playerTables.length);

  if (playerTables.length >= 2) {
    // Extract from first table (our table)
    const ourPlayers = extractPlayersFromTable(playerTables[0]);
    console.log('Our table players found:', ourPlayers);

    // BBO layout is typically: N, W, E, S (reading top to bottom, left to right)
    if (ourPlayers.length >= 4) {
      players.ourTable.N = ourPlayers[0];
      players.ourTable.W = ourPlayers[1];
      players.ourTable.E = ourPlayers[2];
      players.ourTable.S = ourPlayers[3];
    }

    // Extract from second table (their table)
    const theirPlayers = extractPlayersFromTable(playerTables[1]);
    console.log('Their table players found:', theirPlayers);

    if (theirPlayers.length >= 4) {
      players.theirTable.N = theirPlayers[0];
      players.theirTable.W = theirPlayers[1];
      players.theirTable.E = theirPlayers[2];
      players.theirTable.S = theirPlayers[3];
    }
  }

  console.log('Extracted player names:');
  console.log('  Our table - N:', players.ourTable.N, 'E:', players.ourTable.E, 'S:', players.ourTable.S, 'W:', players.ourTable.W);
  console.log('  Their table - N:', players.theirTable.N, 'E:', players.theirTable.E, 'S:', players.theirTable.S, 'W:', players.theirTable.W);
  return players;
}

/**
 * Parse LIN format to extract hand diagrams
 * LIN format: md|3S5AH56KD678JC46JQ,S37JH39TQD39TQC7A,S49TH4JAD25KAC59T,
 * The first digit indicates the first hand (1=S, 2=W, 3=N, 4=E)
 */
function parseLINDeal(linString) {
  // Extract md (deal) parameter from LIN string
  const mdMatch = linString.match(/md\|([^|]+)\|/);
  if (!mdMatch) return null;

  const mdData = mdMatch[1];

  // First character is the starting position (1=S, 2=W, 3=N, 4=E)
  const startPos = parseInt(mdData[0]);
  const positions = ['S', 'W', 'N', 'E'];
  const startIdx = startPos - 1;

  // Split hands by comma
  const handStrings = mdData.substring(1).split(',');

  if (handStrings.length < 3) return null;

  const deal = { N: null, E: null, S: null, W: null };

  // Parse each of the 3 explicitly given hands
  for (let i = 0; i < 3; i++) {
    const position = positions[(startIdx + i) % 4];
    const handStr = handStrings[i];

    if (handStr) {
      deal[position] = parseLINHand(handStr);
    }
  }

  // Calculate the 4th hand from the other 3
  const missingPos = positions[(startIdx + 3) % 4];
  deal[missingPos] = calculateMissingHand(deal);

  return deal;
}

/**
 * Parse a single hand from LIN format
 * Format: S5AH56KD678JC46JQ (suits in SHDC order)
 */
function parseLINHand(handStr) {
  const suits = { spades: '', hearts: '', diamonds: '', clubs: '' };

  let currentSuit = null;
  let currentCards = '';

  for (let i = 0; i < handStr.length; i++) {
    const char = handStr[i];

    if (char === 'S') {
      if (currentSuit) suits[currentSuit] = currentCards;
      currentSuit = 'spades';
      currentCards = '';
    } else if (char === 'H') {
      if (currentSuit) suits[currentSuit] = currentCards;
      currentSuit = 'hearts';
      currentCards = '';
    } else if (char === 'D') {
      if (currentSuit) suits[currentSuit] = currentCards;
      currentSuit = 'diamonds';
      currentCards = '';
    } else if (char === 'C') {
      if (currentSuit) suits[currentSuit] = currentCards;
      currentSuit = 'clubs';
      currentCards = '';
    } else {
      currentCards += char;
    }
  }

  if (currentSuit) {
    suits[currentSuit] = currentCards;
  }

  return suits;
}

/**
 * Calculate the missing hand from the other three
 */
function calculateMissingHand(deal) {
  const allCards = {
    spades: 'AKQJT98765432',
    hearts: 'AKQJT98765432',
    diamonds: 'AKQJT98765432',
    clubs: 'AKQJT98765432'
  };

  const missing = { spades: '', hearts: '', diamonds: '', clubs: '' };

  for (const suit of ['spades', 'hearts', 'diamonds', 'clubs']) {
    const usedCards = new Set();

    for (const pos of ['N', 'E', 'S', 'W']) {
      if (deal[pos] && deal[pos][suit]) {
        for (const card of deal[pos][suit]) {
          usedCards.add(card);
        }
      }
    }

    for (const card of allCards[suit]) {
      if (!usedCards.has(card)) {
        missing[suit] += card;
      }
    }
  }

  return missing;
}

/**
 * Extract hand records from LIN data in board links
 */
function extractHandRecordsFromLinks(doc) {
  const handRecords = {};

  // Find all board links with LIN data
  const boardLinks = doc.querySelectorAll('a[href*="lin="]');

  console.log(`Found ${boardLinks.length} board links with LIN data`);

  for (const link of boardLinks) {
    const href = link.getAttribute('href');

    // Extract board number from the link context
    const boardCell = link.closest('tr')?.querySelector('.boardcell .boardno');
    if (!boardCell) continue;

    const boardNum = boardCell.textContent.trim();
    if (!boardNum || !/^\d+$/.test(boardNum)) continue;

    try {
      // Decode the URL-encoded LIN data
      const linData = decodeURIComponent(href.split('lin=')[1] || '');

      // Parse dealer and vulnerability from LIN
      const dealerMatch = linData.match(/md\|(\d)/);
      const vulnMatch = linData.match(/sv\|([noeban])/i);

      // Convert dealer (1=S, 2=W, 3=N, 4=E)
      const dealerMap = { '1': 'S', '2': 'W', '3': 'N', '4': 'E' };
      const dealer = dealerMap[dealerMatch?.[1]] || 'N';

      // Convert vulnerability
      const vulnMap = {
        'o': 'None',
        'n': 'NS',
        'e': 'EW',
        'b': 'All'
      };
      const vulnerable = vulnMap[vulnMatch?.[1]?.toLowerCase()] || 'None';

      // Parse the deal
      const deal = parseLINDeal(linData);

      if (deal) {
        handRecords[boardNum] = {
          dealer,
          vulnerable,
          deal
        };
      }
    } catch (e) {
      console.warn(`Failed to parse LIN data for board ${boardNum}:`, e);
    }
  }

  console.log(`Extracted hand records for ${Object.keys(handRecords).length} boards`);
  return handRecords;
}

/**
 * Apply name mapping to the players object
 * @param {Object} players - Original players object with ourTable and theirTable
 * @returns {Promise<Object>} - Players object with mapped names
 */
async function applyNameMappingToPlayers(players) {
  const mapped = {
    ourTable: {},
    theirTable: {},
    allPlayers: []
  };

  // Map ourTable players
  if (players.ourTable) {
    for (const [position, name] of Object.entries(players.ourTable)) {
      mapped.ourTable[position] = await mapPlayerNames([name]).then(arr => arr[0]);
    }
  }

  // Map theirTable players
  if (players.theirTable) {
    for (const [position, name] of Object.entries(players.theirTable)) {
      mapped.theirTable[position] = await mapPlayerNames([name]).then(arr => arr[0]);
    }
  }

  // Map allPlayers array
  if (players.allPlayers && Array.isArray(players.allPlayers)) {
    mapped.allPlayers = await mapPlayerNames(players.allPlayers);
  }

  return mapped;
}

/**
 * Calculate team scores with mapped player names
 * @param {Array} boards - Board results
 * @param {Object} matchInfo - Match information
 * @param {Object} mappedPlayers - Mapped player names
 * @returns {Promise<Array>} - Team scores
 */
async function calculateTeamScoresWithMapping(boards, matchInfo, mappedPlayers) {
  let totalIMPs = 0;

  for (const board of boards) {
    totalIMPs += board.imps;
  }

  const numBoards = boards.length || matchInfo.numBoards;
  const vpResult = calculateVPs(totalIMPs, numBoards);

  // Format players for team 1 (NS at our table, EW at their table) - using mapped names
  const team1Players = [];
  if (mappedPlayers.ourTable) {
    const { N, S } = mappedPlayers.ourTable;
    if (N) team1Players.push(N);
    if (S) team1Players.push(S);
  }
  if (mappedPlayers.theirTable) {
    const { E, W } = mappedPlayers.theirTable;
    if (E) team1Players.push(E);
    if (W) team1Players.push(W);
  }

  // Format players for team 2 (EW at our table, NS at their table) - using mapped names
  const team2Players = [];
  if (mappedPlayers.ourTable) {
    const { E, W } = mappedPlayers.ourTable;
    if (E) team2Players.push(E);
    if (W) team2Players.push(W);
  }
  if (mappedPlayers.theirTable) {
    const { N, S } = mappedPlayers.theirTable;
    if (N) team2Players.push(N);
    if (S) team2Players.push(S);
  }

  return [
    {
      position: '1',
      team: '1',
      teamname: matchInfo.team1,
      players: team1Players,
      imps: totalIMPs,
      vps: vpResult.team1VP
    },
    {
      position: '2',
      team: '2',
      teamname: matchInfo.team2,
      players: team2Players,
      imps: -totalIMPs,
      vps: vpResult.team2VP
    }
  ];
}

/**
 * Convert to travellers format with mapped player names
 * @param {Array} boards - Board results
 * @param {Object} mappedPlayers - Mapped player names
 * @returns {Promise<Object>} - Travellers data
 */
async function convertToTravellersWithMapping(boards, mappedPlayers) {
  const travellers = {};

  for (const board of boards) {
    const boardNum = board.boardNumber.toString();

    // Format player pairs with mapped names
    const formatPlayerPair = (table, nsOrEw) => {
      if (!mappedPlayers[table]) return '';

      if (nsOrEw === 'NS') {
        const n = mappedPlayers[table].N || '';
        const s = mappedPlayers[table].S || '';
        return n && s ? `${n} - ${s}` : (n || s || '');
      } else {
        const e = mappedPlayers[table].E || '';
        const w = mappedPlayers[table].W || '';
        return e && w ? `${e} - ${w}` : (e || w || '');
      }
    };

    const result1 = {
      ns: '1',
      ew: '2',
      nsPlayers: formatPlayerPair('ourTable', 'NS'),
      ewPlayers: formatPlayerPair('theirTable', 'EW'),
      contract: board.contract1 || '',
      declarer: board.declarer1 || '',
      made: board.result1 !== undefined ? parseInt(board.result1) : undefined,
      nsScore: board.score1 !== undefined ? board.score1.toString() : '0',
      ewScore: board.score1 !== undefined ? (-board.score1).toString() : '0',
      nsPts: board.imps > 0 ? board.imps : 0,
      ewPts: board.imps < 0 ? Math.abs(board.imps) : 0
    };

    const result2 = {
      ns: '2',
      ew: '1',
      nsPlayers: formatPlayerPair('theirTable', 'NS'),
      ewPlayers: formatPlayerPair('ourTable', 'EW'),
      contract: board.contract2 || '',
      declarer: board.declarer2 || '',
      made: board.result2 !== undefined ? parseInt(board.result2) : undefined,
      nsScore: board.score2 !== undefined ? board.score2.toString() : '0',
      ewScore: board.score2 !== undefined ? (-board.score2).toString() : '0',
      nsPts: board.imps < 0 ? Math.abs(board.imps) : 0,
      ewPts: board.imps > 0 ? board.imps : 0
    };

    travellers[boardNum] = [result1, result2];
  }

  return travellers;
}
